import { useCallback, useEffect, useState } from "react";
import { WalletScreen } from "./screens/Wallet";
import { ReceiveScreen } from "./screens/Receive";
import { SendScreen } from "./screens/Send";
import { SwapScreen } from "./screens/Swap";
import { ContactsScreen } from "./screens/Contacts";
import { BackupScreen } from "./screens/Backup";
import { SecurityScreen } from "./screens/Security";
import { SettingsScreen } from "./screens/Settings";
import { useUnlockedWallet } from "./wallet";
import { useShieldedKeys } from "./shielded/keys-context";
import { BottomNavContext, type BottomNavKey } from "./components/atoms";
import { hapticSelection } from "./lib/haptics";
import { readStartParam } from "./lib/startparam";
import { PayInvoiceScreen } from "./screens/PayInvoice";

interface SendPrefill {
  unifiedAddress: string;
  username: string | null;
}

export default function App() {
  const wallet = useUnlockedWallet();
  useShieldedKeys(); // mounts the derivation effect; result is consumed inside screens
  const [tab, setTabState] = useState<BottomNavKey>("wallet");
  // Wrap the tab setter so every bottom-nav tap (and programmatic
  // tab swap via context consumers) emits a selection-changed
  // haptic. Only buzzes when the destination differs from the
  // current tab — keeps the feel honest.
  const setTab = useCallback((next: BottomNavKey) => {
    setTabState((prev) => {
      if (prev !== next) hapticSelection();
      return next;
    });
  }, []);
  const [sendPrefill, setSendPrefill] = useState<SendPrefill | null>(null);
  // Backup screen is modal — not a tab. Triggered from the Wallet
  // banner or the "Show recovery phrase" link.
  const [backupOpen, setBackupOpen] = useState(false);
  // Shieldz Payments deep-link: when the WebApp launches with
  // `start_param=pay_<public_id>`, we render the PayInvoice screen on top
  // of everything else. Read once at mount; clearing it returns the user
  // to the normal tabbed flow.
  const [payInvoiceId, setPayInvoiceId] = useState<string | null>(() => {
    const sp = readStartParam();
    return sp && sp.kind === "pay_invoice" ? sp.invoicePublicId : null;
  });

  // Initialise Telegram WebApp when available (no-op in browser preview).
  // We also pipe TG's themeParams into CSS variables so the wallet
  // doesn't look like a dark island on a user with a light TG theme.
  useEffect(() => {
    const w = window.Telegram?.WebApp as
      | (TelegramWebApp & {
          themeParams?: Record<string, string>;
          colorScheme?: "light" | "dark";
          onEvent?: (event: string, cb: () => void) => void;
          offEvent?: (event: string, cb: () => void) => void;
        })
      | undefined;
    if (!w) return;
    try {
      w.ready();
      w.expand();
    } catch {
      // older clients may not support all APIs
    }

    const applyTheme = () => {
      const tp = w.themeParams ?? {};
      const scheme = w.colorScheme ?? "dark";
      const bg = tp.bg_color ?? (scheme === "light" ? "#FFFFFF" : "#0E1116");
      const txt = tp.text_color ?? (scheme === "light" ? "#0A0C10" : "#FFFFFF");
      const hint = tp.hint_color;
      const link = tp.link_color;
      const btn = tp.button_color;
      const btnTxt = tp.button_text_color;

      const root = document.documentElement;
      root.style.setProperty("--bg", bg);
      root.style.setProperty("--text", txt);
      if (hint) root.style.setProperty("--hint", hint);
      if (link) root.style.setProperty("--link", link);
      if (btn) root.style.setProperty("--btn-primary-bg", btn);
      if (btnTxt) root.style.setProperty("--btn-primary-fg", btnTxt);

      // Toggle light theme on body for the existing .theme-light overrides
      // in styles.css to take effect.
      if (scheme === "light") {
        document.body.classList.add("theme-light");
      } else {
        document.body.classList.remove("theme-light");
      }

      try {
        w.setHeaderColor?.(bg);
        w.setBackgroundColor?.(bg);
      } catch {
        // ignore
      }
    };
    applyTheme();
    // TG fires "themeChanged" if the user toggles theme mid-session.
    w.onEvent?.("themeChanged", applyTheme);
    return () => {
      w.offEvent?.("themeChanged", applyTheme);
    };
  }, []);

  // Bind TG chrome's native SettingsButton — the gear that appears in
  // the WebApp's "..." menu on recent clients. When tapped, switch the
  // active tab to settings. Older clients omit this API entirely, so
  // every call is wrapped in try/catch and the cleanup is best-effort.
  useEffect(() => {
    const wa = window.Telegram?.WebApp as
      | (TelegramWebApp & {
          SettingsButton?: {
            show?: () => void;
            hide?: () => void;
            onClick?: (cb: () => void) => void;
            offClick?: (cb: () => void) => void;
          };
        })
      | undefined;
    const sb = wa?.SettingsButton;
    if (!sb) return;
    const handler = () => setTab("settings");
    try {
      sb.show?.();
    } catch {
      /* older client */
    }
    try {
      sb.onClick?.(handler);
    } catch {
      /* older client */
    }
    return () => {
      try {
        sb.offClick?.(handler);
      } catch {
        /* noop */
      }
      try {
        sb.hide?.();
      } catch {
        /* noop */
      }
    };
  }, [setTab]);

  if (!wallet) return null;

  // Modal overlay — pre-empts the tabbed view so the backup flow
  // doesn't share screen state with whatever's underneath.
  if (backupOpen) {
    return <BackupScreen onClose={() => setBackupOpen(false)} />;
  }

  // Shieldz Payments deep-link overlay. Same modal pattern as backup —
  // pre-empts the tab view until the buyer either finishes or backs out.
  if (payInvoiceId) {
    return (
      <PayInvoiceScreen
        publicId={payInvoiceId}
        onBack={() => setPayInvoiceId(null)}
      />
    );
  }

  // Pick the active screen.
  const screen = (() => {
    switch (tab) {
      case "wallet":
        return <WalletScreen onOpenBackup={() => setBackupOpen(true)} />;
      case "send":
        return (
          <SendScreen
            initialRecipient={sendPrefill?.unifiedAddress ?? ""}
            initialUsername={sendPrefill?.username ?? null}
            onBack={() => {
              setSendPrefill(null);
              setTab("wallet");
            }}
          />
        );
      case "swap":
        return <SwapScreen onBack={() => setTab("wallet")} />;
      case "contacts":
        return (
          <ContactsScreen
            onPickRecipient={(p) => {
              setSendPrefill(p);
              setTab("send");
            }}
          />
        );
      case "receive":
        return <ReceiveScreen />;
      case "security":
        return <SecurityScreen />;
      case "settings":
        return <SettingsScreen onOpenBackup={() => setBackupOpen(true)} />;
    }
  })();

  return (
    <BottomNavContext.Provider value={setTab}>
      {screen}
    </BottomNavContext.Provider>
  );
}
